import * as React from "react";
import SearchForm from "../../../containers/SearchForm";
import { Form, KeyValue, Query } from "../../../interfaces";
import {
  Typography,
  List,
  ListItem,
  Divider,
  ThemeProvider,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import SelectFilterField from "../../SelectFilterField";
import { useState } from "react";
import { marketShareReportTypeOptions } from "../../../refdata/constants";
import useAppStateToFormChanges from "../../../hooks/useAppStateToFormChanges";
import {
  getExpandState,
  reduceObj,
  yearChoice,
} from "../../../common/services/Utilities";
import useLastYearFilter from "../../../hooks/useLastYearFilter";
import useMinLengthErrorValidation from "../../../hooks/useMinLengthErrorValidation";
import MarketPresenceSearchForm from "./MarketPresence/MarketPresenceSearchForm";
import DepositMarketShareFormOptions from "./DepositMarketShare/DepositMarketShareFormOptions";
import PFATheme from "../../../PFATheme";
import { initialAppState } from "../../../common/reducers/appState";
import AccordionDropdown from "../../../containers/AccordionDropdown";
import BankBHCsToggle from "../../BankBHCsToggle";
import GrowthRateSearchForm from "../../../containers/GrowthRateSearchForm";

export interface MarketShareForm extends Form {
  reportType: string;
  lastYear: string;
  startDate: string;
  endDate: string;
  displayResults: string;
  stalp: string;
  name: string;
  institutionType: string;
  displayAll: boolean;
  locations: string;
  stalpbr: string;
  stateNames: string[];
  msa: string;
  marketSelection: KeyValue[];
  viewBCHandInt: boolean;
  zipbr: string;
}
const useStyles = makeStyles(theme => ({
  title: { paddingTop: 16, lineHeight: "1rem" },
}));

const divider = (
  <Divider
    style={{ margin: 0, backgroundColor: "#acc8df" }}
    variant="middle"
    component="div"
  />
);

export default function MarketShareSearchForm({
  appState,
  updateFilter,
  updateUi,
}: {
  appState: Query;
  updateFilter: (filterField: string, value: string | number) => void;
  updateUi: (field: string, value: string | KeyValue[]) => void;
}) {
  const classes = useStyles();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [prevMarketSelection, setPrevMarketSelection] = React.useState<
    KeyValue[]
  >([]);
  const { lastYear } = useLastYearFilter(appState.filters, updateFilter);

  const filters = {
    lastYear: appState.filters.lastYear,
    endDate: appState.filters.endDate,
    startDate: appState.filters.startDate,
    stalp: appState.filters.stalp,
    stalpbr: appState.filters.stalpbr,
    msa: appState.filters.msa,
    stateNames: appState.filters.stateNames,
    bhcRSSD: appState.filters.bhcRSSD,
    zipbr: appState.filters.zipbr,
  };
  const search = {
    name: appState.search.name,
    bhcName: appState.search.bhcName,
  };

  const initialFormState = {
    ...filters,
    ...search,
    reportType: appState.ui.reportType,
    displayResults: appState.ui.displayResults,
    institutionType: appState.ui.institutionType,
    displayAll: appState.ui.displayAll,
    locations: appState.ui.locations,
    marketSelection: appState.ui.marketSelection,
    viewBCHandInt: appState.ui.viewBCHandInt,
  };

  const [marketShareForm, setMarketShareForm] = useState<MarketShareForm>(
    initialFormState,
  );

  const marketShareErrorMetaData = {
    name: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
    },
    cert: {
      minLength: 1,
    },
    bhcName: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
    },
    bhcRSSD: {
      minLength: 7,
      helperText: "Must be a 7 Digit Number",
    },
  };

  useMinLengthErrorValidation(
    marketShareForm,
    marketShareErrorMetaData,
    setErrors,
  );
  function searchFormObj(currentForm: string[]) {
    return reduceObj(
      Object.keys(marketShareForm).map((key: string) =>
        currentForm.includes(key)
          ? { key, value: marketShareForm[key] }
          : { key, value: defaultSODState[key] },
      ),
    );
  }
  const defaultSODState = {
    ...initialAppState.SOD.filters,
    ...initialAppState.SOD.search,
    ...initialAppState.SOD.ui,
  };

  //Deposit Market Share
  const depositMarketValues = [
    "lastYear",
    "reportType",
    "locations",
    "institutionType",
  ];

  const depositMarketSelectValues =
    marketShareForm.locations === "marketSelect"
      ? ["marketSelection", "stateNames"]
      : [];

  const depositCountyState =
    marketShareForm.locations === "countyByState" ? ["stalpbr"] : [];

  const depositMsas = marketShareForm.locations === "msa" ? ["msa"] : [];

  const currentDepositValues = [
    ...depositMarketValues,
    ...depositMarketSelectValues,
    ...depositCountyState,
    ...depositMsas,
  ];

  //Pro Forma
  const proFormaValues = [
    "lastYear",
    "reportType",
    "locations",
    "institutionType",
  ];

  const proFormaSelectValues =
    marketShareForm.locations === "marketSelect"
      ? ["marketSelection", "stateNames"]
      : [];

  const proFormaMsas = marketShareForm.locations === "msa" ? ["msa"] : [];

  const proFormaZip = marketShareForm.locations === "zip" ? ["zipbr"] : [];
  const currentProFormaValues = [
    ...proFormaValues,
    ...proFormaSelectValues,
    ...proFormaZip,
    ...proFormaMsas,
  ];

  //Market Presence
  const marketPresenceCountyState =
    marketShareForm.displayResults === "marketSelect" ? ["statesByCounty"] : [];

  const currentMarketPresenceValues = [
    "lastYear",
    "reportType",
    "institutionType",
    "displayResults",
    "displayAll",
    "stalpbr",
    "cert",
    "stalpbr",
    "name",
    "bhcName",
    "bhcRSSD",
    ...marketPresenceCountyState,
  ];

  const currentGrowthRateValues = [
    "lastYear",
    "startDate",
    "endDate",
    "reportType",
    "institutionType",
    "displayResults",
    // "displayAll",
    "cert",
    "msa",
    // "name",
    "name",
    "bhcName",
    "bhcRSSD",
    "stalpbr",
  ];

  const currentMarketShareForm: KeyValue = {
    marketPresence: searchFormObj(currentMarketPresenceValues),
    depositMarketShare: searchFormObj(currentDepositValues),
    growthRate: searchFormObj(currentGrowthRateValues),
    proForma: searchFormObj(currentProFormaValues),
  };

  //clears out state filter on msa search
  React.useEffect(() => {
    if (appState.filters.stalpbr && appState.ui.displayResults === "msas") {
      updateFilter("stalpbr", "");
    }
  }, [appState.filters.stalpbr, appState.ui.displayResults, updateFilter]);

  React.useEffect(() => {
    const reportTypeLookup: KeyValue = {
      depositMarketShare: [
        marketShareForm.stalpbr,
        marketShareForm.msa,
        marketShareForm.stateNames[0],
      ],
      marketPresence: [
        marketShareForm.name,
        marketShareForm.cert,
        marketShareForm.bhcName,
        marketShareForm.bhcRSSD,
      ],
      growthRate: [
        marketShareForm.name,
        marketShareForm.cert,
        marketShareForm.bhcName,
        marketShareForm.bhcRSSD,
      ],
      proForma: [
        marketShareForm.stalpbr,
        marketShareForm.msa,
        marketShareForm.stateNames[0],
        marketShareForm.zipbr,
      ],
    };
    const listOfPossibleNull = reportTypeLookup[marketShareForm.reportType]
      ? reportTypeLookup[marketShareForm.reportType]
      : [""];
    const altDisable =
      !marketShareForm.reportType ||
      listOfPossibleNull.every((val: any) => val === "");
    setDisabled(errors.size > 0 || altDisable);
  }, [
    errors.size,
    marketShareForm.bhcName,
    marketShareForm.bhcRSSD,
    marketShareForm.cert,
    marketShareForm.endDate,
    marketShareForm.msa,
    marketShareForm.name,
    marketShareForm.reportType,
    marketShareForm.stalpbr,
    marketShareForm.startDate,
    marketShareForm.stateNames,
    marketShareForm.zipbr,
  ]);
  const marketSharesReportingPeriods = yearChoice(1994, lastYear, "06/30/");

  const { prevForm, setPrevForm } = useAppStateToFormChanges<MarketShareForm>(
    appState.filters,
    appState.search,
    setMarketShareForm,
  );
  const bankFormValues = ["cert", "name"];
  const bhcFormValues = ["bhcName", "bhcRSSD"];

  const institutionTypeSelection = (
    <AccordionDropdown
      id="institutionType"
      title="Institution Type"
      defaultExpand={getExpandState(appState.ui.institutionTypeExpand, true)}
      body={
        <BankBHCsToggle
          form={marketShareForm}
          setForm={setMarketShareForm}
          bankValues={bankFormValues}
          bhcValues={bhcFormValues}
        />
      }
    />
  );

  React.useEffect(() => {
    if (
      marketShareForm.reportType === "depositMarketShare" &&
      marketShareForm.reportType !== appState.ui.reportType &&
      !prevMarketSelection.length
    ) {
      setPrevMarketSelection(marketShareForm.marketSelection);
      setMarketShareForm({
        ...marketShareForm,
        marketSelection: [{}],
      });
    } else if (
      marketShareForm.reportType === appState.ui.reportType &&
      prevMarketSelection.length
    ) {
      setMarketShareForm({
        ...marketShareForm,
        marketSelection: prevMarketSelection,
      });
      setPrevMarketSelection([]);
    }
  }, [appState.ui.reportType, marketShareForm, prevMarketSelection]);

  const MarketShareFormList = (
    <List>
      <ListItem>
        <Typography className={classes.title} variant="h4">
          Report Type
        </Typography>
        <SelectFilterField
          currentValue={marketShareForm.reportType}
          fieldName="reportType"
          options={marketShareReportTypeOptions}
          form={marketShareForm}
          setForm={setMarketShareForm}
          onChange={(e: any) =>
            setMarketShareForm({
              ...marketShareForm,
              reportType: e.target.value,
              stalpbr: "",
              stateNames: [""],
              marketSelection: [{}],
            })
          }
        />
      </ListItem>
      {divider}
      {(marketShareForm.reportType === "depositMarketShare" ||
        marketShareForm.reportType === "proForma") &&
        institutionTypeSelection}
      {marketShareForm.reportType === "marketPresence" && (
        <MarketPresenceSearchForm
          appState={appState}
          searchForm={marketShareForm}
          setSearchForm={setMarketShareForm}
          formMetaData={marketShareErrorMetaData}
          errors={errors}
        />
      )}
      {marketShareForm.reportType === "growthRate" && (
        <GrowthRateSearchForm
          searchForm={marketShareForm}
          setSearchForm={setMarketShareForm}
          formMetaData={marketShareErrorMetaData}
          errors={errors}
        />
      )}
      {marketShareForm.reportType !== "growthRate" && (
        <ListItem>
          <Typography className={classes.title} variant="h4">
            Reporting Period Ending
          </Typography>
          <SelectFilterField
            currentValue={marketShareForm.lastYear}
            fieldName="lastYear"
            options={marketSharesReportingPeriods}
            form={marketShareForm}
            setForm={setMarketShareForm}
          />
        </ListItem>
      )}
      {divider}
      {(marketShareForm.reportType === "depositMarketShare" ||
        marketShareForm.reportType === "proForma") && (
        <DepositMarketShareFormOptions
          appState={appState}
          searchForm={marketShareForm}
          setSearchForm={setMarketShareForm}
          disabled={false}
          setDisabled={setDisabled}
        />
      )}
    </List>
  );
  return (
    <ThemeProvider theme={PFATheme}>
      <SearchForm
        scroll
        prevForm={prevForm}
        noChangeSubmit
        setPrevForm={setPrevForm}
        canHideSearchPanel={false}
        formList={MarketShareFormList}
        disabled={disabled}
        form={
          currentMarketShareForm[marketShareForm.reportType]
            ? currentMarketShareForm[marketShareForm.reportType]
            : defaultSODState
        }
        searchButtonText={"Generate Report"}
      />
    </ThemeProvider>
  );
}
